import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bug, Lightbulb, Heart, MessageCircle, Send, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "bug", label: "Bug / Error", icon: Bug, accent: "text-[#DC2626]" },
  { value: "sugerencia", label: "Sugerencia", icon: Lightbulb, accent: "text-[#D97706]" },
  { value: "elogio", label: "Elogio", icon: Heart, accent: "text-[#16A34A]" },
  { value: "otro", label: "Otro", icon: MessageCircle, accent: "text-[#0052FF]" },
];

const MIN_LEN = 5;
const MAX_LEN = 2000;

export default function FeedbackModal({ open, onOpenChange }) {
  const [category, setCategory] = useState("sugerencia");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCategory("sugerencia");
      setContent("");
    }
  }, [open]);

  const remaining = MAX_LEN - content.length;
  const tooShort = content.trim().length < MIN_LEN;

  const submit = async () => {
    if (tooShort || submitting) return;
    setSubmitting(true);
    try {
      await api.post(
        "/feedback",
        { category, content: content.trim() },
        { headers: { "X-Finly-Page": window.location.pathname } }
      );
      toast.success("¡Gracias! Tu feedback fue enviado.");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo enviar el feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  const ActiveIcon = (CATEGORIES.find((c) => c.value === category) || CATEGORIES[1]).icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="feedback-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#F1F5F9]">
            <ActiveIcon className="w-5 h-5 text-[#0052FF]" />
            Enviar feedback
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            Cuéntanos qué piensas. Tu mensaje queda asociado a tu cuenta para que podamos darle seguimiento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
              Categoría
            </label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="feedback-category-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  return (
                    <SelectItem
                      key={c.value}
                      value={c.value}
                      data-testid={`feedback-category-${c.value}`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${c.accent}`} />
                        {c.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">
              Mensaje
            </label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value.slice(0, MAX_LEN))}
              placeholder="Describe el bug, la sugerencia o cuéntanos qué te gustó…"
              rows={6}
              maxLength={MAX_LEN}
              data-testid="feedback-content-input"
              className="resize-none"
            />
            <div className="flex items-center justify-between text-[11px] text-[#64748B]">
              <span data-testid="feedback-min-hint">
                {tooShort ? `Mínimo ${MIN_LEN} caracteres` : "\u00A0"}
              </span>
              <span data-testid="feedback-char-counter">{remaining}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="feedback-cancel-btn"
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={tooShort || submitting}
            className="bg-[#0052FF] hover:bg-[#0046DB] text-white"
            data-testid="feedback-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Enviando…
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-1.5" /> Enviar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
